import { PlanWorkflowStep, Role } from '@/const.js';
import { useAuthStore } from '@/store';

const {
  WorkInProgress1, WorkInProgress2, WorkInProgress3,
  SubmittedForCouncilNoting, SubmittedForMecReview,
  DraftApproval, SubmittedForCouncilApproval,
  SubmittedToMec, FinalApproval, PublishedAsFinal,
} = PlanWorkflowStep;

const VIEWER_BANNERS = {
  [WorkInProgress1]: null,
  [SubmittedForCouncilNoting]: null,
  [SubmittedForMecReview]: null,
  [WorkInProgress2]: null,
  [DraftApproval]: null,
  [WorkInProgress3]: 'The <strong>draft</strong> document is ready for review.',
  [SubmittedForCouncilApproval]: 'The <strong>draft</strong> document is ready for review.',
  [SubmittedToMec]: 'The <strong>draft</strong> document is ready for review.',
  [FinalApproval]: 'The <strong>draft</strong> document is ready for review.',
  [PublishedAsFinal]: 'The <strong>final</strong> document is ready for review.',
};

const IDP_OFFICER_BANNERS = {
  [WorkInProgress1]: null,
  [SubmittedForCouncilNoting]: 'Document submitted for council noting - awaiting Municipal Administrator confirmation.',
  [SubmittedForMecReview]: 'Awaiting submission to MEC by Municipal Administrator.',
  [WorkInProgress2]: 'Following approval from the Council and MEC, the document is now open for editing and refinements.',
  [DraftApproval]: 'The <strong>draft</strong> has been submitted to the Municipal Administrator for approval and publication.',
  [WorkInProgress3]: 'Now that the draft is published, please continue refining the content for the final version.',
  [SubmittedForCouncilApproval]: 'Document submitted for council for approval - awaiting Municipal Administrator confirmation.',
  [SubmittedToMec]: 'Awaiting submission to MEC by Municipal Administrator.',
  [FinalApproval]: 'The <strong>final</strong> document has been submitted to the Municipal Administrator for approval and publication.',
  [PublishedAsFinal]: 'The document is approved. Both the draft and final versions are now published.',
};

const MUNI_ADMIN_BANNERS = {
  [WorkInProgress1]: null,
  [SubmittedForCouncilNoting]: 'The document is ready for Council noting. Please review the content to approve or reject the submission.',
  [SubmittedForMecReview]: 'Confirm when the document has been submitted to the MEC for review.',
  [WorkInProgress2]: 'The document has been noted by Council and MEC.<br>Please continue with changes and refinements.',
  [DraftApproval]: 'Document is ready to be published. Please review and approve or reject.',
  [WorkInProgress3]: 'The draft document has been published. Please continue by making changes and refinements.',
  [SubmittedForCouncilApproval]: 'Document is ready for Council Approval. Please review.',
  [SubmittedToMec]: 'Confirm submission to the MEC.',
  [FinalApproval]: 'The document is ready to be published as a final document. Please review.',
  [PublishedAsFinal]: 'The document has been approved and published as a draft and a final document.',
};

const SYS_ADMIN_BANNERS = {
  [WorkInProgress1]: null,
  [SubmittedForCouncilNoting]: 'Document submitted for council noting - awaiting Municipal Administrator confirmation.',
  [SubmittedForMecReview]: 'Document submitted for MEC Review - awaiting Municipal Administrator confirmation.',
  [WorkInProgress2]: 'Following approval from the Council and MEC, the document is now open for editing and refinements.',
  [DraftApproval]: 'The <strong>draft</strong> has been submitted for approval and publication.',
  [WorkInProgress3]: 'Now that the draft is published, please continue refining the content for the final version.',
  [SubmittedForCouncilApproval]: 'Document submitted for council approval - awaiting Municipal Administrator confirmation.',
  [SubmittedToMec]: 'Document submitted for MEC Review - awaiting Municipal Administrator confirmation.',
  [FinalApproval]: 'The <strong>final</strong> document has been submitted to the Municipal Administrator for approval and publication.',
  [PublishedAsFinal]: 'The document has been approved and published as a draft and a final document.',
};

function getViewerButtons(workflowStep) {
  if (workflowStep === PublishedAsFinal) return { downloadDraft: true, downloadFinal: true };
  return { viewPdf: true };
}

function getIdpOfficerButtons(workflowStep) {
  if ([WorkInProgress1, WorkInProgress2].includes(workflowStep)) return { edit: true, viewPdf: true, notifyAdminToSubmit: true };
  if ([SubmittedForCouncilNoting, SubmittedForMecReview, DraftApproval, SubmittedForCouncilApproval, SubmittedToMec, FinalApproval].includes(workflowStep)) return { viewPdf: true };
  if (workflowStep === WorkInProgress3) return { edit: true, viewPdf: true, notifyAdminToApprove: true };
  if (workflowStep === PublishedAsFinal) return { downloadDraft: true, downloadFinal: true };
}

function getMunicipalAdminButtons(workflowStep) {
  if ([WorkInProgress1, WorkInProgress2, WorkInProgress3].includes(workflowStep)) return { edit: true, viewPdf: true, submitForApproval: true };
  if (workflowStep === SubmittedForCouncilNoting) return { viewPdf: true, approveDraftForCouncil: true, reopenForEditing: true };
  if (workflowStep === SubmittedForMecReview) return { viewPdf: true, submitForFirstMec: true, reopenForEditing: true };
  if (workflowStep === DraftApproval) return { viewPdf: true, approve: true, rejectDraft: true };
  if (workflowStep === SubmittedForCouncilApproval) return { viewPdf: true, approveFinalForCouncil: true, rejectFinal: true };
  if (workflowStep === SubmittedToMec) return { viewPdf: true, submitForFinalMec: true, reopenForEditing: true };
  if (workflowStep === FinalApproval) return { viewPdf: true, approveFinalAndPublish: true, rejectFinal: true };
  if (workflowStep === PublishedAsFinal) return { downloadDraft: true, downloadFinal: true };
}

function getSysAdminButtons(workflowStep) {
  if ([WorkInProgress1, WorkInProgress2, WorkInProgress3].includes(workflowStep)) return { edit: true, viewPdf: true };
  if ([SubmittedForCouncilNoting, SubmittedForMecReview, DraftApproval, SubmittedForCouncilApproval, SubmittedToMec, FinalApproval].includes(workflowStep)) return { viewPdf: true };
  if (workflowStep === PublishedAsFinal) return { downloadDraft: true, downloadFinal: true };
}

export function usePlanOverviewRole() {
  const authStore = useAuthStore();

  const getActiveRoleContent = (workflowStep) => {
    const roleConfig = {
      [Role.SystemAdministrator]: {
        description: 'As a System Administrator, you manage the platform\'s maintenance, municipal registry, frameworks, and user roles. You also have full authority to view and edit IDP plans for your linked municipalities.',
        banner: SYS_ADMIN_BANNERS[workflowStep],
        buttons: getSysAdminButtons(workflowStep),
      },
      [Role.MunicipalAdministrator]: {
        description: 'As a Municipal Administrator, you have access to edit, or approve the IDP plan for this municipality.',
        banner: MUNI_ADMIN_BANNERS[workflowStep],
        buttons: getMunicipalAdminButtons(workflowStep),
      },
      [Role.IdpOfficer]: {
        description: 'As an IDP Officer, you are the primary author and compiler of the IDP. You have full access to create and edit sections in Draft status, manage annexures, and input all project, financial, and SDBIP data.',
        banner: IDP_OFFICER_BANNERS[workflowStep],
        buttons: getIdpOfficerButtons(workflowStep),
      },
      [Role.Viewer]: {
        description: 'As a viewer, you are able to read through draft documents.',
        banner: VIEWER_BANNERS[workflowStep],
        buttons: getViewerButtons(workflowStep),
      },
    };

    // Users can hold multiple roles — precedence order determines which config is applied
    const ROLE_PRECEDENCE = [Role.MunicipalAdministrator, Role.IdpOfficer, Role.SystemAdministrator, Role.Viewer];
    const role = ROLE_PRECEDENCE.find(r => authStore.user.roles.includes(r) && roleConfig[r]);
    return roleConfig[role];
  };

  return { getActiveRoleContent };
}
